import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Card, CardContent, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { acceptInvitation, resetPassword } from '../api/authFlows'
import { errorMessage } from '../api/client'
import { useAuth } from '../auth/AuthContext'

/**
 * Servește ambele fluxuri — acceptare invitație (FR-ADM-020) și resetare de
 * parolă (FR-ADM-001k) — pentru că UI-ul e identic: „introdu o parolă nouă",
 * diferă doar textul și endpoint-ul apelat.
 */
export default function SetPasswordPage({ mode }: { mode: 'invite' | 'reset' }) {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const navigate = useNavigate()
  const { adoptSession } = useAuth()

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)

    if (password.length < 8) { setError('Parola trebuie să aibă minimum 8 caractere.'); return }
    if (password !== confirm) { setError('Parolele nu coincid.'); return }
    if (!token) { setError('Link invalid — lipsește tokenul.'); return }

    setBusy(true)
    try {
      const user = mode === 'invite' ? await acceptInvitation(token, password) : await resetPassword(token, password)
      adoptSession(user)
      navigate('/', { replace: true })
    } catch (err) {
      setError(errorMessage(err, 'Link invalid sau expirat.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', bgcolor: 'background.default', p: 2 }}>
      <Card variant="outlined" sx={{ width: '100%', maxWidth: 400 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h3" sx={{ mb: 0.5 }}>
            {mode === 'invite' ? 'Activare cont' : 'Resetare parolă'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {mode === 'invite'
              ? 'Alegeți o parolă pentru a vă activa contul CIPRAL.'
              : 'Alegeți o parolă nouă pentru contul dvs.'}
          </Typography>

          <form onSubmit={(e) => void submit(e)}>
            <Stack spacing={2}>
              <TextField label="Parolă nouă" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} autoFocus required fullWidth
                helperText="Minimum 8 caractere" />
              <TextField label="Confirmă parola" type="password" value={confirm}
                onChange={(e) => setConfirm(e.target.value)} required fullWidth />
              {error && <Alert severity="error" variant="outlined">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" fullWidth disabled={busy}
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}>
                {mode === 'invite' ? 'Activează contul' : 'Setează parola'}
              </Button>
            </Stack>
          </form>

          <Typography variant="body2" sx={{ mt: 2.5 }}>
            <Link to="/">Înapoi la autentificare</Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}
